/**
 * Danh sách TỪNG JOB của Hậu kỳ / CSKH.
 *
 * ⚠️ Bảng này KHÔNG mang tiền lương. Sổ từng job đang chạy song song với số
 * tổng trưởng phòng gõ tay; lương vẫn tính theo số gõ tay. Nên ở đây chỉ hiện
 * số lượng và doanh thu của từng job, và nói rõ điều đó ở dòng ghi chú — chứ
 * không cộng ra một con số trông như tiền lương thứ hai.
 */

import { useState } from "react";
import { vnd } from "../../constants";
import { shortDate } from "../../salaryDetail";
import type { WorkLedgerRow } from "../../workLedgerApi";
import { kindLabel } from "../../workLedgerLib";
import { LineTable } from "./primitives";

/** Quá số này thì gấp lại — bảng 200 dòng đẩy hết mọi mục khác ra khỏi màn hình. */
const COLLAPSE_AFTER = 12;

/**
 * Cột tiền chỉ hiện khi loại việc CÓ tiền. Hậu kỳ trả theo đầu ảnh nên mọi
 * dòng đều 0đ — hiện một cột toàn số 0 cạnh chữ "doanh thu" là mời người đọc
 * hiểu nhầm rằng công của họ không đáng gì.
 */
export function WorkLedgerLines({
  rows,
  showMoney,
}: {
  rows: WorkLedgerRow[];
  showMoney: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!rows.length) return null;

  const shown = expanded ? rows : rows.slice(0, COLLAPSE_AFTER);
  const hidden = rows.length - shown.length;

  return (
    <>
      <LineTable
        columns={[
          { key: "date", label: "Ngày" },
          { key: "customer", label: "Khách" },
          { key: "code", label: "Mã đơn", className: "hidden md:table-cell" },
          { key: "kind", label: "Loại việc", className: "hidden md:table-cell" },
          {
            key: "qty",
            label: showMoney ? "SL" : "Số ảnh",
            className: "text-right",
          },
          ...(showMoney
            ? [{ key: "amount", label: "Doanh thu", className: "text-right" }]
            : []),
        ]}
      >
        {shown.map((r) => (
          <tr key={r.id}>
            <td className="whitespace-nowrap px-1 py-1.5 tabular-nums md:px-2">
              {shortDate(r.shootDate)}
            </td>
            <td className="px-1 py-1.5 md:px-2">
              {r.customer || "—"}
              <span className="block text-xs text-muted-foreground md:hidden">
                {kindLabel(r.kind)}
                {r.source === "manual" ? " · đã sửa tay" : ""}
              </span>
            </td>
            <td className="hidden px-2 py-1.5 text-muted-foreground md:table-cell">
              {r.orderCode || "—"}
            </td>
            <td className="hidden px-2 py-1.5 md:table-cell">
              {kindLabel(r.kind)}
              {r.source === "manual" ? (
                <span
                  title={r.note || "Dòng do người sửa tay"}
                  className="ml-1 text-xs text-muted-foreground"
                >
                  ✎
                </span>
              ) : null}
            </td>
            <td className="px-1 py-1.5 text-right tabular-nums md:px-2">
              {r.qty || "—"}
            </td>
            {showMoney ? (
              <td className="px-1 py-1.5 text-right tabular-nums md:px-2">
                {vnd(r.amount)}
              </td>
            ) : null}
          </tr>
        ))}
      </LineTable>

      {hidden > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-2 text-xs text-primary underline underline-offset-2"
        >
          Xem thêm {hidden} dòng nữa
        </button>
      ) : null}
    </>
  );
}

/**
 * Câu giải thích mối quan hệ giữa sổ và số gõ tay, đặt dưới mỗi bảng.
 *
 * Nói thẳng khi hai bên lệch nhau. Giấu chỗ lệch đi thì người đọc phát hiện
 * ra sau, và lần đó họ mất tin vào cả bản kê chứ không chỉ một con số.
 */
export function LedgerNote({
  ledgerCount,
  typedLabel,
  mismatch,
}: {
  ledgerCount: number;
  typedLabel: string;
  mismatch: boolean;
}) {
  if (!ledgerCount) {
    return (
      <>
        Số lượng là tổng cả tháng do trưởng phòng chốt ({typedLabel}). Sổ từng
        job của tháng này chưa có dòng nào — báo trưởng phòng chạy lại sổ.
      </>
    );
  }
  return (
    <>
      Lương tính theo số tổng trưởng phòng chốt ({typedLabel}). Bảng dưới là sổ
      từng job, để bạn tự đối chiếu.
      {mismatch ? (
        <>
          {" "}
          <b className="text-amber-700">
            Hai số đang lệch nhau
          </b>{" "}
          — nếu bạn thấy thiếu job nào, báo trưởng phòng bổ sung.
        </>
      ) : null}
    </>
  );
}
